import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { FiHome, FiShoppingCart, FiBox, FiSettings, FiLogOut, FiCreditCard, FiFileText, FiMenu, FiX } from 'react-icons/fi';

function SideNavBar({ user, onLogout }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const displayName = user?.name || user?.email || 'Account';

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) &&
          !event.target.closest('[data-mobile-menu-toggle]')) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const NavItems = () => (
    <>
      {user?.role_id == '1' && (
          <NavLink
            to="/home"
            onClick={() => setIsMobileMenuOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                isActive
                  ? 'border-maroon-700 bg-maroon-700 text-white'
                  : 'border-maroon-700 bg-transparent text-maroon-100 hover:bg-maroon-900'
              }`
            }
          >
            <FiHome /> Dashboard
          </NavLink>
        )}
        <NavLink
          to="/pos"
          onClick={() => setIsMobileMenuOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
              isActive
                ? 'border-maroon-700 bg-maroon-700 text-white'
                : 'border-maroon-700 bg-transparent text-maroon-100 hover:bg-maroon-900'
            }`
          }
        >
          <FiShoppingCart /> POS
        </NavLink>
        <NavLink
          to="/items"
          onClick={() => setIsMobileMenuOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
              isActive
                ? 'border-maroon-700 bg-maroon-700 text-white'
                : 'border-maroon-700 bg-transparent text-maroon-100 hover:bg-maroon-900'
            }`
          }
        >
          <FiBox /> Items
        </NavLink>

        {user?.role_id == '1' && (
        <NavLink
          to="/types"
          onClick={() => setIsMobileMenuOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
              isActive
                ? 'border-maroon-700 bg-maroon-700 text-white'
                : 'border-maroon-700 bg-transparent text-maroon-100 hover:bg-maroon-900'
            }`
          }
        >
          <FiCreditCard /> Payment Type
        </NavLink>
      )}

      <NavLink
        to="/sales"
        onClick={() => setIsMobileMenuOpen(false)}
        className={({ isActive }) =>
          `flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
            isActive
              ? 'border-maroon-700 bg-maroon-700 text-white'
              : 'border-maroon-700 bg-transparent text-maroon-100 hover:bg-maroon-900'
          }`
        }
      >
        <FiFileText /> Sales
      </NavLink>
    </>
  );

  return (
    <>
      {/* Mobile Header */}
      <div className="flex items-center justify-between bg-maroon-950 px-4 py-3 text-maroon-50 md:hidden">
        <div className="text-2xl font-extrabold text-white tracking-wide">Kanaiya</div>
        <button
          data-mobile-menu-toggle
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="rounded-lg border border-maroon-700 p-2 text-maroon-100 hover:bg-maroon-900"
        >
          {isMobileMenuOpen ? <FiX size={20} /> : <FiMenu size={20} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 md:hidden" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      {/* Mobile Menu Sidebar */}
      <div
        ref={mobileMenuRef}
        className={`fixed left-0 top-0 z-50 flex h-full w-64 flex-col bg-maroon-950 px-4 py-5 text-maroon-50 transition-transform duration-300 md:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="mb-6 text-3xl font-extrabold text-white tracking-wide">Kanaiya</div>
        <nav className="flex flex-grow flex-col gap-2 overflow-y-auto">
          <NavItems />
        </nav>
        <div className="relative mt-auto" ref={menuRef}>
          <button
            className="flex items-center gap-2 w-full rounded-lg border border-maroon-700 bg-maroon-900 px-3 py-2 text-sm font-semibold text-maroon-50 hover:bg-maroon-800"
            onClick={() => setIsMenuOpen((prev) => !prev)}
          >
            <span className="inline-block w-6 h-6 rounded-full bg-maroon-700 flex items-center justify-center text-xs font-bold">
              {displayName.charAt(0).toUpperCase()}
            </span>
            {displayName}
          </button>
          {isMenuOpen && (
            <div className="absolute left-0 right-0 bottom-14 z-20 rounded-lg border border-maroon-100 bg-white shadow-lg">
              <button className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-maroon-900 hover:bg-maroon-50">
                <FiSettings /> Settings
              </button>
              <button
                className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-maroon-900 hover:bg-maroon-50"
                onClick={onLogout}
              >
                <FiLogOut /> Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:w-64 md:flex-col md:sticky md:top-0 md:h-screen bg-maroon-950 px-4 py-5 text-maroon-50 md:overflow-y-auto">
        <div className="mb-6 text-3xl font-extrabold text-white tracking-wide">Kanaiya</div>
        <nav className="flex flex-grow flex-col gap-2">
          <NavItems />
        </nav>
        <div className="relative mt-auto" ref={menuRef}>
          <button
            className="flex items-center gap-2 w-full rounded-lg border border-maroon-700 bg-maroon-900 px-3 py-2 text-sm font-semibold text-maroon-50 hover:bg-maroon-800"
            onClick={() => setIsMenuOpen((prev) => !prev)}
          >
            <span className="inline-block w-6 h-6 rounded-full bg-maroon-700 flex items-center justify-center text-xs font-bold">
              {displayName.charAt(0).toUpperCase()}
            </span>
            {displayName}
          </button>
          {isMenuOpen && (
            <div className="absolute left-0 right-0 bottom-14 z-20 rounded-lg border border-maroon-100 bg-white shadow-lg">
              <button className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-maroon-900 hover:bg-maroon-50">
                <FiSettings /> Settings
              </button>
              <button
                className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-maroon-900 hover:bg-maroon-50"
                onClick={onLogout}
              >
                <FiLogOut /> Logout
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

export default SideNavBar;
